import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const { user, profile, isDealer, isAdmin, loading: authLoading, signOut } = useAuth()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const isActive = (path) => location.pathname === path

  const navItems = [
    { path: '/', label: 'Главная' },
    { path: '/catalog', label: 'Каталог' },
    { path: '/about', label: 'О нас' },
    { path: '/contacts', label: 'Контакты' },
  ]

  return (
    <>
      <header
        className={[
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          'bg-white/80 backdrop-blur-md border-b border-ss-border',
          scrolled ? 'shadow-[0_2px_8px_rgba(0,0,0,0.04)]' : '',
        ].join(' ')}
      >
        <div className="mx-auto max-w-ss px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center select-none" aria-label="SwedenSound">
              <span className="text-xl font-semibold text-ss-text">Sweden</span>
              <span className="text-xl font-light text-ss-dark">Sound</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={[
                    'relative text-sm font-medium tracking-wide transition-colors duration-300',
                    isActive(item.path) ? 'text-ss-dark' : 'text-ss-text hover:text-ss-dark',
                  ].join(' ')}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-ss-dark rounded-full" />
                  )}
                </Link>
              ))}

              {/* Auth area */}
              <div className="flex items-center gap-3 pl-4 border-l border-ss-border">
                {!authLoading && user && (
                  <div className="flex items-center gap-2">
                    {isDealer && (
                      <span className="inline-flex items-center rounded-full bg-ss-dark px-2.5 py-1 text-xs font-medium text-white">
                        Дилер
                      </span>
                    )}
                    {isAdmin && (
                      <span className="inline-flex items-center rounded-full bg-ss-text px-2.5 py-1 text-xs font-medium text-white">
                        Админ
                      </span>
                    )}
                    <Link
                      to={isAdmin ? '/admin' : '/dashboard'}
                      className="text-sm text-ss-text hover:text-ss-dark transition-colors"
                    >
                      {isAdmin ? 'Админ-панель' : 'Личный кабинет'}
                    </Link>
                  </div>
                )}
                {!authLoading && !user && (
                  <Link
                    to="/login"
                    className="inline-flex items-center rounded-full border border-ss-border px-4 py-1.5 text-sm font-medium text-ss-text hover:bg-ss-gray hover:border-ss-dark transition-all duration-300"
                  >
                    Вход для дилеров
                  </Link>
                )}
              </div>
            </nav>

            {/* Mobile burger */}
            <button
              className="md:hidden p-2 text-ss-dark"
              aria-label="Открыть меню"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer */}
      <div
        className={[
          'fixed top-0 right-0 bottom-0 z-[70] w-72 bg-white shadow-xl',
          'transform transition-transform duration-300 ease-out',
          mobileOpen ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
        aria-label="Мобильное меню"
      >
        <div className="flex items-center justify-between p-4 border-b border-ss-border">
          <span className="text-lg font-semibold text-ss-text">Sweden</span>
          <span className="text-lg font-light text-ss-dark">Sound</span>
          <button
            className="p-2 text-ss-text hover:text-ss-dark"
            aria-label="Закрыть меню"
            onClick={() => setMobileOpen(false)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col p-4 gap-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={[
                'px-4 py-3 rounded-xl text-base font-medium transition-colors',
                isActive(item.path)
                  ? 'bg-ss-gray text-ss-dark'
                  : 'text-ss-text hover:bg-ss-gray hover:text-ss-dark',
              ].join(' ')}
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </Link>
          ))}

          {/* Mobile auth */}
          <div className="mt-4 pt-4 border-t border-ss-border">
            {!authLoading && user && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-4 py-2">
                  {isDealer && (
                    <span className="inline-flex items-center rounded-full bg-ss-dark px-2.5 py-1 text-xs font-medium text-white">
                      Дилер
                    </span>
                  )}
                  {isAdmin && (
                    <span className="inline-flex items-center rounded-full bg-ss-text px-2.5 py-1 text-xs font-medium text-white">
                      Админ
                    </span>
                  )}
                  <span className="text-sm text-ss-text truncate">
                    {isAdmin ? 'Админ-панель' : 'Личный кабинет'}
                  </span>
                </div>
                <Link
                  to={isAdmin ? '/admin' : '/dashboard'}
                  className="block px-4 py-3 rounded-xl text-base font-medium text-ss-text hover:bg-ss-gray hover:text-ss-dark transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Личный кабинет
                </Link>
                <button
                  onClick={() => {
                    signOut()
                    setMobileOpen(false)
                  }}
                  className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-ss-muted hover:bg-ss-gray hover:text-ss-dark transition-colors"
                >
                  Выйти
                </button>
              </div>
            )}
            {!authLoading && !user && (
              <Link
                to="/login"
                className="block px-4 py-3 rounded-xl text-base font-medium text-ss-dark hover:bg-ss-gray transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Вход для дилеров
              </Link>
            )}
          </div>
        </nav>
      </div>
    </>
  )
}
